import axios from 'axios';
import mockDb from '../data/mockDb.json';
import config from '../config/config';

// In real implementation, these would be your API endpoints
const BASE_URL = config.API_BASE_URL;;

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor for JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Mock API service
class ApiService {
  constructor() {
    this.db = { ...mockDb };
  }

  // Simulate API delay
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Helper method to save DB state
  saveState() {
    // In a real application, this would be an API call
    console.log('Current DB State:', this.db);
  }

  async login(credentials) {
    await this.delay(500);
    // Mock login logic
    if (credentials.username === 'admin' && credentials.password === 'admin') {
      return {
        token: 'mock-jwt-token',
        user: {
          id: 1,
          username: credentials.username,
          name: 'Admin User'
        }
      };
    }
    throw new Error('Invalid credentials');
  }

  async findOrder(orderId) {
    await this.delay(500);
    const order = this.db.orders.find(o => o.orderId === orderId);
    if (!order) throw new Error('Order not found');
    return { ...order }; // Return a copy
  }

  async findLoad(loadNumber) {
    await this.delay(500);
    const load = this.db.loads.find(l => l.loadNumber === loadNumber);
    if (!load) throw new Error('Load not found');
    return { ...load }; // Return a copy
  }

  async createLoad(loadData) {
    await this.delay(800);
    const newLoad = {
      ...loadData,
      orders: [loadData.orderId]
    };

    // Update the database immutably
    this.db = {
      ...this.db,
      loads: [...this.db.loads, newLoad],
      orders: this.db.orders.map(order => 
        order.orderId === loadData.orderId
          ? { ...order, loadNumber: loadData.loadNumber }
          : order
      )
    };

    this.saveState();
    return { ...newLoad };
  }

  async assignOrderToLoad(orderId, loadNumber) {
    await this.delay(600);
    const load = this.db.loads.find(l => l.loadNumber === loadNumber);
    if (!load) throw new Error('Load not found');

    const orderIndex = this.db.orders.findIndex(o => o.orderId === orderId);
    if (orderIndex === -1) throw new Error('Order not found');

    // Create new objects instead of mutating
    const updatedLoad = {
      ...load,
      orders: [...load.orders, orderId]
    };

    const updatedOrder = {
      ...this.db.orders[orderIndex],
      loadNumber
    };

    // Update the database immutably
    this.db = {
      ...this.db,
      loads: this.db.loads.map(l => 
        l.loadNumber === loadNumber ? updatedLoad : l
      ),
      orders: this.db.orders.map(o => 
        o.orderId === orderId ? updatedOrder : o
      )
    };

    this.saveState();
    return updatedOrder;
  }
}

// Create a single instance of ApiService
const apiService = new ApiService();

// Export the instance
export { apiService as api };

export default api; 